/**
 * Wall-clock resolution in a named IANA timezone.
 *
 * WHY THIS EXISTS. Time windows in this codebase (guest access hours,
 * tariff TOU, owner utility TOU) store a bare hour 0-23 and a day
 * 0-6 with NO zone attached. Until 2026-08-09 every server-side
 * evaluator read those integers with `Date.getDay()` / `getHours()`,
 * which is the SERVER's local time — UTC on Fargate. An owner in
 * Toronto who set 9:00-22:00 had it enforced as 9-22 UTC, i.e.
 * 5 AM-6 PM their time. Every owner was setting a different window
 * from the one they saw.
 *
 * The stored integers were never wrong: they are uninterpreted
 * wall-clock values whose zone is supplied at evaluation. Supplying
 * the CHARGER LOCATION's zone (rather than the server's) makes them
 * finally mean what the owner typed. No data migration — see
 * docs/tickets/ and the backfill script for the reasoning.
 *
 * NO RUNTIME DEPENDENCY. `Intl.DateTimeFormat` ships with Node and
 * every browser, carries the full IANA database, and handles DST
 * transitions correctly — which a stored UTC offset cannot (the
 * carbon layer's JURISDICTION_UTC_OFFSET is standard-time only and
 * would be an hour wrong all summer). Coordinate -> IANA lookup DOES
 * need a dependency, but only in the one-off backfill script, never
 * at runtime.
 */
const WEEKDAY_INDEX = {
    Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6,
};
/** Formatter construction is the expensive part; cache per zone. */
const formatterCache = new Map();
function formatterFor(timeZone) {
    const hit = formatterCache.get(timeZone);
    if (hit)
        return hit;
    const fmt = new Intl.DateTimeFormat('en-US', {
        timeZone,
        weekday: 'short',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    });
    formatterCache.set(timeZone, fmt);
    return fmt;
}
/**
 * True when `timeZone` is a zone this runtime can actually resolve.
 * Callers persist only validated zones, so a typo can never silently
 * become a wrong-by-an-hour enforcement rule.
 */
export function isValidTimeZone(timeZone) {
    if (!timeZone)
        return false;
    try {
        new Intl.DateTimeFormat('en-US', { timeZone });
        return true;
    }
    catch {
        return false;
    }
}
/**
 * The wall clock in `timeZone` at instant `at`.
 *
 * Falls back to the SERVER's local reading when the zone is unusable
 * (absent or invalid). That fallback is the pre-fix behavior and is
 * deliberately visible to callers via `resolveWindowZone` below —
 * never a silent guess.
 */
export function wallClockIn(at, timeZone) {
    if (!timeZone || !isValidTimeZone(timeZone)) {
        return {
            day: at.getDay(),
            hour: at.getHours(),
            minute: at.getMinutes(),
            year: at.getFullYear(),
            month: at.getMonth() + 1,
            dayOfMonth: at.getDate(),
        };
    }
    const parts = formatterFor(timeZone).formatToParts(at);
    const get = (t) => parts.find((p) => p.type === t)?.value ?? '';
    // hourCycle h23 can render midnight as '24' in some ICU versions.
    const rawHour = Number(get('hour'));
    return {
        day: WEEKDAY_INDEX[get('weekday')] ?? at.getDay(),
        hour: rawHour === 24 ? 0 : rawHour,
        minute: Number(get('minute')),
        year: Number(get('year')),
        month: Number(get('month')),
        dayOfMonth: Number(get('day')),
    };
}
/**
 * The UTC instant at which a given wall-clock moment occurs in
 * `timeZone`. Used to turn "next Wednesday at 09:00 local" into the
 * absolute instant the PWA renders.
 *
 * Method: guess the instant as if the wall clock were UTC, read what
 * that instant actually looks like in the zone, and correct by the
 * observed delta. Two passes converge for every real zone including
 * DST boundaries; on a spring-forward gap (a wall time that does not
 * exist) it settles on the instant just after the jump, which is the
 * behavior a "window opens at" moment wants.
 */
export function instantForWallClock(parts, timeZone) {
    const asUtc = Date.UTC(parts.year, parts.month - 1, parts.dayOfMonth, parts.hour, parts.minute ?? 0, 0, 0);
    if (!timeZone || !isValidTimeZone(timeZone)) {
        // Server-local fallback: build with the local constructor so the
        // result matches the pre-fix convention exactly.
        return new Date(parts.year, parts.month - 1, parts.dayOfMonth, parts.hour, parts.minute ?? 0, 0, 0);
    }
    let instant = asUtc;
    for (let i = 0; i < 2; i += 1) {
        const seen = wallClockIn(new Date(instant), timeZone);
        const seenAsUtc = Date.UTC(seen.year, seen.month - 1, seen.dayOfMonth, seen.hour, seen.minute, 0, 0);
        const drift = asUtc - seenAsUtc;
        if (drift === 0)
            break;
        instant += drift;
    }
    return new Date(instant);
}
/**
 * Short zone label for UI copy — "ET", "PT", or the IANA city when
 * the runtime has no short name. Never invents one.
 */
export function timeZoneShortLabel(timeZone, locale = 'en-US', at = new Date()) {
    if (!timeZone || !isValidTimeZone(timeZone))
        return null;
    try {
        const parts = new Intl.DateTimeFormat(locale, { timeZone, timeZoneName: 'short' })
            .formatToParts(at);
        const name = parts.find((p) => p.type === 'timeZoneName')?.value;
        if (name)
            return name;
    }
    catch {
        // fall through to the city form
    }
    const city = timeZone.split('/').pop();
    return city ? city.replace(/_/g, ' ') : timeZone;
}
//# sourceMappingURL=localTime.js.map